import { Alert, Modal, Spin } from "antd";
import { Button as CommonButton } from "src/Components";
import { Header } from "src/Interfaces/TableInterfaces";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import {
  BaseExecutionOfferPreview,
  BaseExecutionOfferPreviewRow,
  BaseExecutionTaskRow,
} from "src/Requests/baseMarketMaking";

const topHeaders: Header[] = [
  { headerName: "account", translateHeaderName: "اکانت", positionTypeStr: "FirstPage" },
  { headerName: "symbol", translateHeaderName: "نماد", positionTypeStr: "FirstPage" },
  { headerName: "orderType", translateHeaderName: "نوع سفارش", positionTypeStr: "FirstPage" },
  { headerName: "referencePrice", translateHeaderName: "قیمت مبنا", positionTypeStr: "FirstPage" },
  { headerName: "factor", translateHeaderName: "ضریب", positionTypeStr: "FirstPage" },
  { headerName: "fairPrice", translateHeaderName: "قیمت تعادلی", positionTypeStr: "FirstPage" },
  { headerName: "pricePermitRange", translateHeaderName: "قیمت مجاز", positionTypeStr: "FirstPage" },
];

const offerHeaders: Header[] = [
  { headerName: "calculatedPrice", translateHeaderName: "قیمت محاسباتی", positionTypeStr: "FirstPage" },
  { headerName: "calculatedVolume", translateHeaderName: "حجم محاسباتی", positionTypeStr: "FirstPage" },
  { headerName: "finalVolume", translateHeaderName: "حجم نهایی", positionTypeStr: "FirstPage" },
];

const formatNumber = (value: number | null) => value == null ? "-" : value.toLocaleString("en-US", {
  maximumFractionDigits: 0,
});
const formatFactor = (value: number | null) => value == null ? "-" : value.toLocaleString("en-US", {
  maximumFractionDigits: 6,
});
const formatPriceRange = (min: number | null, max: number | null) =>
  min != null && max != null && min > 0 && max >= min
    ? `${formatNumber(max)} - ${formatNumber(min)}` : "-";
const formatPercent = (value: number | null, signed = false) => value == null ? "-" :
  `${signed && value > 0 ? "+" : ""}${value.toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 4,
  })}%`;

const offerReport = (rows: BaseExecutionOfferPreviewRow[], offset: boolean, hasPriceRange: boolean) => ({
  report: rows.map((row, index) => ({
    id: row.rowNo, row: index + 1,
    calculatedPrice: formatNumber(row.calculatedPrice),
    calculatedVolume: formatNumber(row.calculatedVolume),
    finalVolume: row.disabledByPriceLimit ? hasPriceRange
      ? "غیرفعال (خارج از بازه قیمت مجاز)" : "غیرفعال (قیمت مجاز نامشخص)" :
      row.disabledByBuyingPower ? "غیرفعال (قدرت خرید)" :
      offset ? "هنگام ارسال" : formatNumber(row.finalVolume),
    calculatedPriceClassname: row.disabledByBuyingPower || row.disabledByPriceLimit ? "!bg-slate-100 !text-slate-400" : "",
    calculatedVolumeClassname: row.disabledByBuyingPower || row.disabledByPriceLimit ? "!bg-slate-100 !text-slate-400" : "",
    finalVolumeClassname: row.disabledByBuyingPower || row.disabledByPriceLimit ? "!bg-slate-100 !text-slate-400" : "",
  })),
  translatedHeaders: offerHeaders,
});

export const canSendPreview = (item: BaseExecutionOfferPreview) =>
  !item.fairPriceChangeExceeded && item.snapshotId != null && item.programId != null && item.fairPrice != null &&
  item.fairPrice > 0 && item.buy.length > 0 && item.sell.length > 0 &&
  [...item.buy, ...item.sell].filter(row => !row.disabledByBuyingPower && !row.disabledByPriceLimit).some(row =>
    (item.orderType === "Offset" ? row.calculatedVolume : row.finalVolume ?? 0) > 0 &&
    (row.calculatedPrice ?? 0) > 0);

interface Props {
  action: "play" | "resend";
  open: boolean;
  loading: boolean;
  submitting: boolean;
  previews: BaseExecutionOfferPreview[] | null;
  tasks: BaseExecutionTaskRow[];
  onClose: () => void;
  onConfirm: () => void;
}

const ExecutionPlayPreview = ({ open, loading, submitting, previews, tasks, action,
  onClose, onConfirm }: Props) => <Modal
    open={open} title={action === "play" ? "پیش‌نمایش فعال‌سازی تسک پایه" : "پیش‌نمایش ارسال مجدد تسک پایه"}
    width="min(1080px, calc(100vw - 32px))"
    onCancel={onClose} footer={<div className="flex justify-end gap-2" dir="rtl">
      <div className="w-24"><CommonButton variant="outlined" text="انصراف" onClick={onClose} /></div>
      <div className="w-24"><CommonButton variant="contained" text="ارسال" loading={submitting}
        disabled={loading || !previews?.some(canSendPreview)}
        onClick={onConfirm} /></div>
    </div>}>
    <div className="max-h-[70vh] overflow-y-auto space-y-6 py-3" dir="rtl">
      {loading && <div className="py-10 text-center"><Spin /></div>}
      {!loading && previews?.map(preview => {
        const task = tasks.find(item => item.id === preview.taskId);
        return <section key={preview.taskId} className="space-y-4">
          <CommonTableUI reports={{
            report: [{ id: preview.taskId, row: 1, account: preview.account,
              symbol: task?.symbol || preview.instrumentId,
              orderType: preview.orderType,
              referencePrice: formatNumber(preview.referencePrice),
              factor: formatFactor(preview.factor),
              fairPrice: formatNumber(preview.fairPrice),
              pricePermitRange: preview.priceLimitsBypassed ? "اعمال نمی‌شود" :
                formatPriceRange(preview.minPricePermit, preview.maxPricePermit) }],
            translatedHeaders: topHeaders,
          }} tableBoxClassName="big-font-table" />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div><div className="mb-2 text-center font-semibold">خرید</div>
              <CommonTableUI reports={offerReport(preview.buy, preview.orderType === "Offset",
                preview.minPricePermit != null && preview.maxPricePermit != null)}
                tableBoxClassName="big-font-table" isRowClickable={() => false} /></div>
            <div><div className="mb-2 text-center font-semibold">فروش</div>
              <CommonTableUI reports={offerReport(preview.sell, preview.orderType === "Offset",
                preview.minPricePermit != null && preview.maxPricePermit != null)}
                tableBoxClassName="big-font-table" isRowClickable={() => false} /></div>
          </div>
          {preview.isBuyingPowerLimited && <Alert type="warning" showIcon
            message="حداقل قدرت خرید برای سفارش‌گذاری کامل تأمین نشده است."
            description={`فقط سطر اول خرید و فروش فعال است. حد تنظیم‌شده: ${formatNumber(preview.minimumBuyingPower)}. قدرت خرید هنگام ارسال دوباره بررسی می‌شود.`} />}
          {!preview.priceLimitsBypassed && (preview.minPricePermit == null || preview.maxPricePermit == null) &&
            <Alert type="warning" showIcon
              message="بازه قیمت مجاز دریافت نشد؛ سطرها غیرفعال‌اند و ارسال نمی‌شوند." />}
          {preview.minPricePermit != null && preview.maxPricePermit != null &&
            [...preview.buy, ...preview.sell].some(row => row.disabledByPriceLimit) &&
            <Alert type="warning" showIcon
              message="سطرهای خارج از بازه قیمت مجاز غیرفعال‌اند و ارسال نمی‌شوند." />}
          {preview.fairPriceChangeExceeded && <Alert type="error" showIcon
            message="تغییر قیمت تعادلی از سقف تنظیم‌شده بیشتر است؛ این تسک ارسال نمی‌شود."
            description={`قیمت تعادلی آخرین تریگر موفق: ${formatNumber(preview.lastSuccessfulFairPrice)}؛ تغییر فعلی: ${formatPercent(preview.fairPriceChangePercent, true)}؛ سقف مجاز تغییر: ${formatPercent(preview.maxFairPriceChangePercent)}. این کنترل مستقل از بازه قیمت مجاز روز است.`} />}
          {(!preview.buy.length || !preview.sell.length) &&
            <Alert type="warning" showIcon message="جدول تنظیمات ارسال این تسک پر نشده است." />}
          {preview.fairPrice == null &&
            <Alert type="warning" showIcon message="قیمت مبنا یا ضریب معتبر در داده‌های لحظه‌ای موجود نیست؛ قیمت سفارش قابل محاسبه نیست." />}
          {preview.orderType === "Offset" && <Alert type="info" showIcon
            message="حجم نهایی آفست بر اساس موقعیت باز در لحظهٔ ارسال محاسبه می‌شود." />}
        </section>;
      })}
      {!loading && !previews?.length && <Alert type="warning" showIcon
        message="پیش‌نمایشی برای تسک‌های انتخاب‌شده دریافت نشد." />}
    </div>
  </Modal>;

export default ExecutionPlayPreview;
